<div
  *ngIf="loading"
  class="min-h-screen bg-slate-50 flex items-center justify-center"
>
  <div class="flex flex-col items-center gap-3">
    <span class="loading loading-spinner loading-lg text-indigo-600"></span>
    <p class="text-sm text-slate-500 font-semibold m-0">
      Loading tutor profile...
    </p>
  </div>
</div>

<div
  *ngIf="error"
  class="min-h-screen bg-slate-50 flex items-center justify-center p-6"
>
  <div
    class="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm text-center max-w-[400px] w-full"
  >
    <svg
      class="w-16 h-16 text-rose-500 mx-auto mb-4"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="1.5"
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
      />
    </svg>
    <h3 class="text-lg font-bold text-slate-800 mb-1">Profile Error</h3>
    <p class="text-sm text-slate-500 m-0 mb-4">{{ error }}</p>
    <a
      href="/tutors"
      class="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-semibold transition-colors no-underline"
    >
      Back to Directory
    </a>
  </div>
</div>

<div *ngIf="tutor && !loading" class="bg-slate-50 min-h-screen pb-12">
  <!-- Banner Cover -->
  <div class="h-48 md:h-72 w-full bg-slate-200 relative overflow-hidden">
    <img
      *ngIf="tutor.profile?.tutorInfo?.bannerImage"
      [src]="getBannerUrl(tutor.profile?.tutorInfo?.bannerImage)"
      [alt]="tutor.profile?.personal?.firstName + ' cover'"
      class="w-full h-full object-cover"
    />
    <div
      *ngIf="!tutor.profile?.tutorInfo?.bannerImage"
      class="w-full h-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 opacity-80"
    ></div>
    <div
      class="absolute inset-0 bg-gradient-to-t from-slate-900/65 to-transparent pointer-events-none"
    ></div>
  </div>

  <div
    class="max-w-[1200px] mx-auto px-4 md:px-6 relative z-10 -mt-16 md:-mt-24"
  >
    <!-- Header Summary Card -->
    <div
      class="bg-white rounded-3xl p-6 md:p-8 shadow-sm border border-slate-100 mb-8 flex flex-col md:flex-row gap-6 items-start md:items-center"
    >
      <!-- Avatar -->
      <div
        class="relative w-28 h-28 md:w-36 md:h-36 rounded-full border-4 border-white overflow-hidden shadow-md flex-shrink-0 bg-slate-100 flex items-center justify-center"
      >
        <img
          *ngIf="tutor.profile?.personal?.avatar"
          [src]="getAvatarUrl(tutor.profile?.personal?.avatar)"
          [alt]="tutor.profile?.personal?.firstName"
          class="w-full h-full object-cover"
        />
        <div
          *ngIf="!tutor.profile?.personal?.avatar"
          class="w-full h-full bg-primary text-primary-content flex items-center justify-center text-4xl md:text-5xl font-black uppercase"
        >
          {{ tutor.profile?.personal?.firstName?.charAt(0) || 'T' }}
        </div>
      </div>

      <!-- Info -->
      <div class="flex-grow">
        <div class="flex items-center gap-2.5 flex-wrap">
          <h1 class="text-xl md:text-3xl font-extrabold text-slate-900 m-0">
            {{ tutor.profile?.personal?.firstName }}
            {{ tutor.profile?.personal?.lastName }}
          </h1>
          <span
            *ngIf="tutor.profile?.tutorInfo?.isVerifiedTutor"
            class="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] md:text-xs font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full flex items-center gap-1"
          >
            <svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fill-rule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clip-rule="evenodd"
              />
            </svg>
            Verified Instructor
          </span>
        </div>

        <p class="text-sm md:text-lg text-slate-500 font-semibold m-0 mt-1">
          {{ tutor.profile?.professional?.jobTitle || 'Expert Tutor' }}
        </p>

        <div class="flex flex-wrap gap-2 mt-4">
          <span
            class="bg-indigo-50 text-indigo-700 text-xs font-bold px-3 py-1 rounded-full"
            *ngFor="let sub of tutor.profile?.tutorInfo?.subjects || []"
          >
            {{ sub }}
          </span>
        </div>
      </div>

      <!-- General Booking CTA -->
      <div class="flex-shrink-0 w-full md:w-auto">
        <a
          [href]="getWhatsAppLink()"
          target="_blank"
          class="w-full md:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-bold shadow-md hover:shadow-lg transition-all no-underline"
        >
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path
              d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946C.06 5.348 5.397.01 12.008.01c3.202.001 6.212 1.246 8.477 3.514 2.266 2.268 3.507 5.28 3.505 8.484-.004 6.657-5.34 11.997-11.953 11.997-2.005-.001-3.973-.502-5.733-1.455L0 24zm6.59-4.846c1.6.95 3.188 1.449 4.825 1.451 5.436 0 9.86-4.37 9.864-9.799.002-2.63-1.023-5.101-2.885-6.965C16.59 2.019 14.12 1.01 11.816 1.01c-5.452 0-9.877 4.372-9.882 9.8-.001 1.77.476 3.498 1.38 5.048L2.247 21.5l5.808-1.517z"
            />
          </svg>
          Quick WhatsApp Inquiry
        </a>
      </div>
    </div>

    <!-- Grid Columns -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <!-- Main Info -->
      <div class="lg:col-span-2 flex flex-col gap-8">
        <!-- About Section -->
        <div
          class="bg-white rounded-3xl p-6 md:p-8 shadow-sm border border-slate-100"
        >
          <h3
            class="text-lg font-bold text-slate-800 mb-4 pb-2 border-b border-slate-100"
          >
            About the Tutor
          </h3>
          @if (tutor.profile?.personal?.biography) {
            <div
              class="text-sm md:text-base text-slate-600 leading-relaxed m-0 prose max-w-none"
              [innerHTML]="tutor.profile?.personal?.biography"
            ></div>
          } @else {
            <p class="text-sm md:text-base text-slate-600 leading-relaxed m-0">
              No biography details provided yet.
            </p>
          }
        </div>

        <!-- Qualifications -->
        <div
          *ngIf="
            tutor.profile?.professional?.educationalQualifications?.length >
              0 ||
            tutor.profile?.professional?.certifications?.length > 0 ||
            tutor.profile?.professional?.experience
          "
          class="bg-white rounded-3xl p-6 md:p-8 shadow-sm border border-slate-100"
        >
          <h3
            class="text-lg font-bold text-slate-800 mb-4 pb-2 border-b border-slate-100"
          >
            Credentials & Experience
          </h3>

          <div class="flex flex-col gap-4">
            <!-- Experience -->
            <div
              *ngIf="tutor.profile?.professional?.experience"
              class="flex gap-3"
            >
              <span
                class="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0 text-indigo-600"
              >
                <svg
                  class="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </span>
              <div>
                <h5 class="text-sm font-bold text-slate-800 m-0">
                  Teaching Experience
                </h5>
                <p class="text-xs text-slate-500 m-0 mt-0.5">
                  {{ tutor.profile?.professional?.experience }}
                </p>
              </div>
            </div>

            <!-- Qualifications -->
            <div
              *ngIf="
                tutor.profile?.professional?.educationalQualifications?.length >
                0
              "
              class="flex gap-3"
            >
              <span
                class="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0 text-indigo-600"
              >
                <svg
                  class="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222"
                  />
                </svg>
              </span>
              <div>
                <h5 class="text-sm font-bold text-slate-800 mb-1">Education</h5>
                <ul
                  class="list-disc pl-4 text-xs text-slate-500 m-0 flex flex-col gap-1"
                >
                  <li
                    *ngFor="
                      let qual of tutor.profile.professional
                        .educationalQualifications
                    "
                  >
                    {{ qual }}
                  </li>
                </ul>
              </div>
            </div>

            <!-- Certifications -->
            <div
              *ngIf="tutor.profile?.professional?.certifications?.length > 0"
              class="flex gap-3"
            >
              <span
                class="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0 text-indigo-600"
              >
                <svg
                  class="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                  />
                </svg>
              </span>
              <div>
                <h5 class="text-sm font-bold text-slate-800 mb-1">
                  Certifications & Awards
                </h5>
                <ul
                  class="list-disc pl-4 text-xs text-slate-500 m-0 flex flex-col gap-1"
                >
                  <li
                    *ngFor="
                      let cert of tutor.profile.professional.certifications
                    "
                  >
                    {{ cert }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <!-- Video Promo -->
        <div
          *ngIf="youtubeEmbedUrl"
          class="bg-white rounded-3xl p-6 md:p-8 shadow-sm border border-slate-100"
        >
          <h3
            class="text-lg font-bold text-slate-800 mb-4 pb-2 border-b border-slate-100"
          >
            Introductory Video
          </h3>
          <div
            class="relative overflow-hidden w-full pt-[56.25%] rounded-2xl bg-slate-900"
          >
            <iframe
              [src]="youtubeEmbedUrl"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen
              class="absolute top-0 bottom-0 left-0 right-0 w-full h-full"
            ></iframe>
          </div>
        </div>

        <!-- Platform Classes & Courses -->
        <div
          *ngIf="courses.length > 0 || liveClasses.length > 0"
          class="flex flex-col gap-6"
        >
          <h3 class="text-lg md:text-xl font-bold text-slate-800 m-0">
            Live Classes
          </h3>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <!-- Platform Courses -->
            <div
              *ngFor="let course of courses"
              class="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden flex flex-col h-full"
            >
              <div
                class="h-32 bg-slate-200 relative overflow-hidden flex-shrink-0"
              >
                <img
                  [src]="getBannerUrl(course.settings?.bannerImageResource)"
                  [alt]="course.title"
                  class="w-full h-full object-cover"
                />
              </div>
              <div class="p-5 flex flex-col flex-grow">
                <span
                  class="bg-violet-50 text-violet-700 text-[10px] font-bold uppercase px-2 py-0.5 rounded w-fit mb-2"
                  >Platform Course</span
                >
                <h4 class="text-sm font-bold text-slate-800 m-0 line-clamp-2">
                  {{ course.title }}
                </h4>
                <p class="text-[11px] text-slate-500 m-0 mt-1 line-clamp-2">
                  {{ course.details?.descriptionSummary }}
                </p>
                <div
                  class="mt-auto pt-4 flex justify-between items-center border-t border-slate-100"
                >
                  <span class="text-sm font-extrabold text-slate-800"
                    >LKR {{ course.settings?.price | number }}</span
                  >
                  <a
                    [routerLink]="['/courses', course._id || course.id]"
                    class="text-xs font-bold text-indigo-600 hover:text-indigo-800 transition-colors no-underline"
                    >View Details</a
                  >
                </div>
              </div>
            </div>

            <!-- Platform Live Classes -->
            <div
              *ngFor="let liveClass of liveClasses"
              class="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden flex flex-col h-full"
            >
              <div
                class="h-32 bg-slate-200 relative overflow-hidden flex-shrink-0"
              >
                <img
                  [src]="getBannerUrl(liveClass.settings?.bannerImageResource)"
                  [alt]="liveClass.title"
                  class="w-full h-full object-cover"
                />
                <span
                  class="absolute left-3 top-3 bg-indigo-600 text-white text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full shadow-sm"
                >
                  {{ liveClass.details?.classMode || 'ONLINE' }}
                </span>
              </div>
              <div class="p-5 flex flex-col flex-grow">
                <span
                  class="bg-indigo-50 text-indigo-700 text-[10px] font-bold uppercase px-2 py-0.5 rounded w-fit mb-2"
                  >Live Session</span
                >
                <h4 class="text-sm font-bold text-slate-800 m-0 line-clamp-2">
                  {{ liveClass.title }}
                </h4>
                <p class="text-[11px] text-slate-500 m-0 mt-1 line-clamp-2">
                  {{ liveClass.details?.descriptionSummary }}
                </p>
                <div
                  class="mt-auto pt-4 flex justify-between items-center border-t border-slate-100"
                >
                  <span class="text-sm font-extrabold text-slate-800"
                    >LKR {{ liveClass.settings?.price | number }}</span
                  >
                  <a
                    [routerLink]="[
                      '/live-classes',
                      liveClass._id || liveClass.id,
                    ]"
                    class="text-xs font-bold text-indigo-600 hover:text-indigo-800 transition-colors no-underline"
                    >View Details</a
                  >
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- External Physical Classes -->
        <div
          *ngIf="(tutor.profile?.tutorInfo?.externalClasses || []).length > 0"
          class="flex flex-col gap-6"
        >
          <h3 class="text-lg md:text-xl font-bold text-slate-800 m-0">
            Physical Classes
          </h3>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div
              *ngFor="let item of tutor.profile.tutorInfo.externalClasses"
              class="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 flex flex-col h-full border-l-4 border-l-amber-400"
            >
              <div class="flex justify-between items-start mb-2">
                <span
                  class="bg-amber-50 text-amber-800 border border-amber-200 text-[9px] font-bold uppercase px-2 py-0.5 rounded"
                >
                  Physical: {{ item.classType }}
                </span>
                <span
                  class="bg-slate-50 text-slate-500 text-[10px] font-semibold px-2 py-0.5 rounded"
                >
                  {{ item.medium }} Medium
                </span>
              </div>

              <h4 class="text-sm font-bold text-slate-800 m-0 mb-1">
                {{ item.title }}
              </h4>
              <p class="text-xs text-slate-500 m-0 mb-3 font-semibold">
                {{ item.subject }} - {{ item.grade }}
              </p>

              <div
                class="flex flex-col gap-2 text-xs text-slate-600 mb-5 mt-auto"
              >
                <div class="flex items-center gap-1.5">
                  <svg
                    class="w-3.5 h-3.5 text-slate-400 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  <span
                    >{{ item.location?.venue ? item.location.venue + ', ' : ''
                    }}{{ item.location?.city }},
                    {{ item.location?.district }}</span
                  >
                </div>
                <div *ngIf="item.schedule" class="flex items-center gap-1.5">
                  <svg
                    class="w-3.5 h-3.5 text-slate-400 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span>{{ item.schedule }}</span>
                </div>
                <div *ngIf="item.feeDetails" class="flex items-center gap-1.5">
                  <svg
                    class="w-3.5 h-3.5 text-slate-400 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M12 16v-1"
                    />
                  </svg>
                  <span class="font-bold text-slate-800">{{
                    item.feeDetails
                  }}</span>
                </div>
              </div>

              <!-- Enroll via WhatsApp -->
              <a
                [href]="
                  getWhatsAppLink(
                    item.title,
                    item.location?.city,
                    item.location?.venue
                  )
                "
                target="_blank"
                class="w-full inline-flex items-center justify-center gap-2 py-2 px-4 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-xs font-bold transition-all no-underline shadow-sm"
              >
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path
                    d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946C.06 5.348 5.397.01 12.008.01c3.202.001 6.212 1.246 8.477 3.514 2.266 2.268 3.507 5.28 3.505 8.484-.004 6.657-5.34 11.997-11.953 11.997-2.005-.001-3.973-.502-5.733-1.455L0 24zm6.59-4.846c1.6.95 3.188 1.449 4.825 1.451 5.436 0 9.86-4.37 9.864-9.799.002-2.63-1.023-5.101-2.885-6.965C16.59 2.019 14.12 1.01 11.816 1.01c-5.452 0-9.877 4.372-9.882 9.8-.001 1.77.476 3.498 1.38 5.048L2.247 21.5l5.808-1.517z"
                  />
                </svg>
                Enroll via WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- Sidebar details -->
      <div class="lg:col-span-1 flex flex-col gap-6">
        <!-- Quick Stats -->
        <div class="bg-white rounded-3xl p-6 shadow-sm border border-slate-100">
          <h4
            class="text-sm font-bold text-slate-800 uppercase tracking-wider mb-4 pb-2 border-b border-slate-100 m-0"
          >
            Teaching Info
          </h4>

          <div class="flex flex-col gap-4">
            <!-- Formats -->
            <div>
              <p
                class="text-[10px] font-bold text-slate-400 uppercase tracking-wider m-0"
              >
                Class Modes Offered
              </p>
              <div class="flex flex-wrap gap-1 mt-1.5">
                <span
                  *ngFor="
                    let fmt of tutor.profile?.tutorInfo?.classFormats || []
                  "
                  class="bg-slate-50 border border-slate-200 text-slate-600 text-[10px] font-bold px-2 py-0.5 rounded"
                >
                  {{ fmt }}
                </span>
              </div>
            </div>

            <!-- Mediums -->
            <div>
              <p
                class="text-[10px] font-bold text-slate-400 uppercase tracking-wider m-0"
              >
                Medium of Instruction
              </p>
              <div class="flex flex-wrap gap-1 mt-1.5">
                <span
                  *ngFor="let med of tutor.profile?.tutorInfo?.mediums || []"
                  class="bg-indigo-50 border border-indigo-150 text-indigo-700 text-[10px] font-bold px-2 py-0.5 rounded"
                >
                  {{ med }}
                </span>
              </div>
            </div>

            <!-- Grade Levels -->
            <div>
              <p
                class="text-[10px] font-bold text-slate-400 uppercase tracking-wider m-0"
              >
                Grades / Curriculum
              </p>
              <div class="flex flex-wrap gap-1 mt-1.5">
                <span
                  *ngFor="let grd of tutor.profile?.tutorInfo?.grades || []"
                  class="bg-purple-50 border border-purple-150 text-purple-700 text-[10px] font-bold px-2 py-0.5 rounded"
                >
                  {{ grd }}
                </span>
              </div>
            </div>

            <!-- General Fees -->
            <div>
              <p
                class="text-[10px] font-bold text-slate-400 uppercase tracking-wider m-0"
              >
                General Tutoring Rates
              </p>
              <p class="text-sm font-bold text-slate-700 m-0 mt-1">
                {{
                  tutor.profile?.tutorInfo?.ratePerHour
                    ? 'LKR ' +
                      (tutor.profile?.tutorInfo?.ratePerHour | number) +
                      ' / hr'
                    : ''
                }}
                {{
                  tutor.profile?.tutorInfo?.ratePerHour &&
                  tutor.profile?.tutorInfo?.ratePerMonth
                    ? ' | '
                    : ''
                }}
                {{
                  tutor.profile?.tutorInfo?.ratePerMonth
                    ? 'LKR ' +
                      (tutor.profile?.tutorInfo?.ratePerMonth | number) +
                      ' / month'
                    : ''
                }}
                {{
                  !tutor.profile?.tutorInfo?.ratePerHour &&
                  !tutor.profile?.tutorInfo?.ratePerMonth
                    ? 'Contact for rates'
                    : ''
                }}
              </p>
              <p
                *ngIf="tutor.profile?.tutorInfo?.pricingDetails"
                class="text-[10px] text-slate-400 mt-1 mb-0"
              >
                {{ tutor.profile?.tutorInfo?.pricingDetails }}
              </p>
            </div>

            <!-- Locations Covered -->
            <div *ngIf="(tutor.profile?.tutorInfo?.districts || []).length > 0">
              <p
                class="text-[10px] font-bold text-slate-400 uppercase tracking-wider m-0"
              >
                Locations Covered
              </p>
              <p
                class="text-xs text-slate-600 m-0 mt-1.5 font-medium leading-relaxed"
              >
                Available in:
                {{ (tutor.profile?.tutorInfo?.districts).join(', ') }}
              </p>
            </div>
          </div>
        </div>

        <!-- Social Media / Contact -->
        <div class="bg-white rounded-3xl p-6 shadow-sm border border-slate-100">
          <h4
            class="text-sm font-bold text-slate-800 uppercase tracking-wider mb-4 pb-2 border-b border-slate-100 m-0"
          >
            Contact & Socials
          </h4>

          <div class="flex flex-col gap-3">
            <!-- Email -->
            <div class="flex items-center gap-2.5 text-xs text-slate-600">
              <svg
                class="w-4 h-4 text-slate-400 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              <span class="truncate">{{
                tutor.profile?.personal?.email || tutor.email
              }}</span>
            </div>

            <!-- WhatsApp Link -->
            <a
              [href]="getWhatsAppLink()"
              target="_blank"
              class="flex items-center gap-2.5 text-xs text-emerald-600 hover:text-emerald-800 transition-colors no-underline font-semibold"
            >
              <svg
                class="w-4 h-4 text-emerald-500 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946C.06 5.348 5.397.01 12.008.01c3.202.001 6.212 1.246 8.477 3.514 2.266 2.268 3.507 5.28 3.505 8.484-.004 6.657-5.34 11.997-11.953 11.997-2.005-.001-3.973-.502-5.733-1.455L0 24zm6.59-4.846c1.6.95 3.188 1.449 4.825 1.451 5.436 0 9.86-4.37 9.864-9.799.002-2.63-1.023-5.101-2.885-6.965C16.59 2.019 14.12 1.01 11.816 1.01c-5.452 0-9.877 4.372-9.882 9.8-.001 1.77.476 3.498 1.38 5.048L2.247 21.5l5.808-1.517z"
                />
              </svg>
              <span>Message on WhatsApp</span>
            </a>

            <!-- Social Links -->
            <div
              *ngIf="
                tutor.profile?.tutorInfo?.socialLinks?.facebook ||
                tutor.profile?.tutorInfo?.socialLinks?.linkedin ||
                tutor.profile?.tutorInfo?.socialLinks?.website
              "
              class="flex gap-2.5 mt-3 pt-3 border-t border-slate-100"
            >
              <a
                *ngIf="tutor.profile.tutorInfo.socialLinks.facebook"
                [href]="tutor.profile.tutorInfo.socialLinks.facebook"
                target="_blank"
                class="w-8 h-8 rounded-full bg-slate-50 border border-slate-200 hover:bg-indigo-50 hover:text-indigo-600 transition-colors flex items-center justify-center text-slate-500"
              >
                FB
              </a>
              <a
                *ngIf="tutor.profile.tutorInfo.socialLinks.linkedin"
                [href]="tutor.profile.tutorInfo.socialLinks.linkedin"
                target="_blank"
                class="w-8 h-8 rounded-full bg-slate-50 border border-slate-200 hover:bg-indigo-50 hover:text-indigo-600 transition-colors flex items-center justify-center text-slate-500"
              >
                IN
              </a>
              <a
                *ngIf="tutor.profile.tutorInfo.socialLinks.website"
                [href]="tutor.profile.tutorInfo.socialLinks.website"
                target="_blank"
                class="w-8 h-8 rounded-full bg-slate-50 border border-slate-200 hover:bg-indigo-50 hover:text-indigo-600 transition-colors flex items-center justify-center text-slate-500"
              >
                WEB
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
